import React, { useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { api } from "@/lib/api";
import { Search, Sparkles, CheckCircle2, MapPin, Phone, Mail, Globe, Plus, Stethoscope, Building2, Heart } from "lucide-react";
import MarketBriefing from "@/components/MarketBriefing";

const PROP_TYPES = [
  { v: "commercial", l: "Commercial" },
  { v: "medical", l: "Medical Office" },
  { v: "dental", l: "Dental Clinic" },
  { v: "retail", l: "Retail" },
  { v: "office", l: "Office" },
  { v: "industrial", l: "Industrial" },
  { v: "residential", l: "Residential" },
  { v: "mixed", l: "Mixed-use" },
];

// Alberta cities & towns (population-ordered major markets)
const ALBERTA_CITIES = [
  "Calgary", "Edmonton", "Red Deer", "Lethbridge", "Medicine Hat",
  "Fort McMurray (Wood Buffalo)", "Grande Prairie", "Airdrie", "St. Albert",
  "Spruce Grove", "Sherwood Park (Strathcona County)", "Leduc", "Okotoks",
  "Camrose", "Lloydminster", "Cochrane", "Beaumont", "Canmore",
  "Banff", "Chestermere", "Stony Plain", "Brooks", "High River",
  "Sylvan Lake", "Wetaskiwin", "Fort Saskatchewan",
];

// Neighbourhood map — major Alberta cities with their commercial/healthcare-relevant districts
const ALBERTA_NEIGHBOURHOODS = {
  "Calgary": [
    "Beltline", "Kensington", "Inglewood", "Mission", "Bridgeland", "Eau Claire",
    "Downtown Core", "17th Ave SW (Red Mile)", "Mount Royal", "Marda Loop",
    "Hillhurst / Sunnyside", "West Hillhurst", "Quarry Park", "Seton",
    "University District", "Brentwood", "Mahogany", "Deerfoot City",
  ],
  "Edmonton": [
    "Downtown", "Whyte Ave / Old Strathcona", "Oliver", "124 Street / Westmount",
    "Ice District", "South Edmonton Common", "Summerside", "Terwillegar",
    "Windermere", "Garneau", "Rossdale", "Jasper Avenue", "Riverbend",
  ],
  "Red Deer": [
    "Downtown", "Bower", "Clearview", "Kentwood", "Timberlands", "Deer Park",
    "Riverside Meadows", "Gasoline Alley",
  ],
  "Lethbridge": [
    "Downtown", "West Lethbridge", "South Lethbridge", "North Lethbridge",
    "University District", "Henderson Lake",
  ],
  "Medicine Hat": [
    "Downtown", "River Flats", "Ross Glen", "Southlands", "Crescent Heights",
    "Kin Coulee",
  ],
  "Fort McMurray (Wood Buffalo)": [
    "Downtown", "Timberlea", "Thickwood", "Gregoire", "Abasand",
  ],
  "Grande Prairie": [
    "Downtown", "Richmond Industrial Park", "Westpointe", "Patterson Place",
    "Mountview",
  ],
  "Airdrie": ["Downtown", "Kingsview", "Bayside", "Yankee Valley"],
  "St. Albert": ["Downtown / Grandin", "Mission", "Riel", "Erin Ridge", "Oakmont"],
  "Spruce Grove": ["Old Town", "Greenbury", "Westgrove", "McLaughlin"],
  "Sherwood Park (Strathcona County)": ["Baseline Road Corridor", "Centre in the Park", "Salisbury", "Summerwood"],
  "Leduc": ["Downtown", "Telford", "Bridgeport", "West Haven"],
  "Canmore": ["Downtown", "Bow Valley Trail", "Spring Creek", "Three Sisters"],
  "Banff": ["Banff Avenue", "Tunnel Mountain"],
  "Cochrane": ["Downtown / Historic Cochrane", "Riviera", "Sunset Ridge", "Fireside"],
};

const SIGNALS = [
  { v: "vacant", l: "Vacant" },
  { v: "expiring_lease", l: "Expiring lease" },
  { v: "closed_business", l: "Closed business" },
  { v: "off_market", l: "Off-market" },
  { v: "owner_retiring", l: "Owner retiring" },
  { v: "expansion", l: "Expansion signals" },
  { v: "distressed", l: "Distressed" },
  { v: "new_listing", l: "New listing" },
];

const DENTAL_SIGNALS = [
  { v: "retiring_dentists", l: "Retiring dentists" },
  { v: "for_sale", l: "For sale" },
  { v: "bankrupt", l: "Bankrupt" },
  { v: "permanently_closed", l: "Permanently closed" },
  { v: "multi_location", l: "Multi-location" },
  { v: "solo_practice", l: "Solo practice" },
];

const fmtCurrency = (n, code = "USD") =>
  n ? new Intl.NumberFormat(code === "CAD" ? "en-CA" : "en-US",
        { style: "currency", currency: code, maximumFractionDigits: 0 }).format(n) : null;

const ScoreBadge = ({ score }) => {
  const n = Number(score || 0);
  const cls = n >= 75 ? "bg-emerald-100 text-emerald-800" : n >= 50 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700";
  return <span className={`text-xs px-2 py-0.5 font-bold uppercase tracking-widest ${cls}`}>AI · {n}</span>;
};

const CandidateCard = ({ c, selected, onToggle, kind }) => {
  const name = c.business_name || c.practice_name || c.contact_person || "—";
  return (
    <div
      onClick={onToggle}
      className={`group bg-white border p-5 cursor-pointer transition ${selected ? "border-gold shadow-luxury" : "border-slate-200 hover:border-navy"}`}
      data-testid="ai-candidate-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="overline text-gold">{c.signal || (kind === "dental" ? c.practice_type : c.property_type)}</div>
          <h3 className="font-display text-xl text-navy mt-1">{name}</h3>
          {(c.owner_name || c.contact_person) && <div className="text-xs text-slate-500 mt-1">Contact: {c.contact_person || c.owner_name}</div>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <ScoreBadge score={c.lead_score} />
          <div className={`w-6 h-6 border-2 flex items-center justify-center ${selected ? "bg-gold border-gold" : "border-slate-300"}`}>
            {selected && <CheckCircle2 className="w-4 h-4 text-navy" />}
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-1 text-xs text-slate-600">
        {c.address && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-gold" /> {c.address}</div>}
        {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-gold" /> {c.phone}</div>}
        {c.email && <div className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-gold flex-shrink-0" /> <span className="truncate">{c.email}</span></div>}
        {c.website && <div className="flex items-center gap-1.5 truncate"><Globe className="w-3 h-3 text-gold flex-shrink-0" /> <span className="truncate">{c.website}</span></div>}
      </div>
      {(c.estimated_price_usd || c.approx_annual_collections_usd) && (
        <div className="mt-3 font-display text-lg text-navy">
          {fmtCurrency(c.estimated_price_usd || c.approx_annual_collections_usd, c.currency || "USD")}
          {c.approx_annual_collections_usd && <span className="ml-1 text-[10px] text-slate-400 tracking-widest uppercase">/ yr collections</span>}
        </div>
      )}
      {c.score_rationale && <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 italic leading-relaxed">"{c.score_rationale}"</div>}
    </div>
  );
};

const LeadSearch = () => {
  const [mode, setMode] = useState("property");  // property | dental | owner
  const [prop, setProp] = useState({ city: "Calgary", state: "Alberta", country: "Canada", property_type: "commercial", business_type: "", keywords: "", limit: 6 });
  const [propSignals, setPropSignals] = useState([]);
  const [propNbhds, setPropNbhds] = useState([]);
  const [dental, setDental] = useState({ city: "", state: "", limit: 6 });
  const [dentalFocus, setDentalFocus] = useState(["retiring_dentists", "for_sale"]);
  const [owner, setOwner] = useState({ business_or_property_name: "", address: "", industry: "" });

  const [candidates, setCandidates] = useState([]);
  const [ownerResult, setOwnerResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [err, setErr] = useState("");

  const toggle = (idx) => {
    const n = new Set(selected);
    n.has(idx) ? n.delete(idx) : n.add(idx);
    setSelected(n);
  };

  const run = async () => {
    setBusy(true); setErr(""); setSelected(new Set()); setCandidates([]); setOwnerResult(null); setSaved(null);
    try {
      if (mode === "property") {
        const { data } = await api.post("/ai/property-finder", { ...prop, signals: propSignals, neighbourhoods: propNbhds });
        setCandidates(data.candidates || []);
      } else if (mode === "dental") {
        const { data } = await api.post("/ai/dental-broker", { ...dental, focus: dentalFocus });
        setCandidates(data.candidates || []);
      } else {
        const { data } = await api.post("/ai/owner-finder", owner);
        setOwnerResult(data);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "AI agent failed");
    } finally { setBusy(false); }
  };

  // Allow market briefing to jump selection
  const handleNeighbourhoodPick = (city, name) => {
    setMode("property");
    setProp((p) => ({ ...p, city }));
    setPropNbhds((prev) => prev.includes(name) ? prev : [...prev, name]);
    // Scroll to filter card for visibility
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const saveToLeads = async () => {
    const picks = Array.from(selected).map((i) => candidates[i]);
    if (picks.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/save-leads", { candidates: picks });
      setSaved(data.inserted);
      setSelected(new Set());
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const toggleSig = (s, list, setList) => {
    setList(list.includes(s) ? list.filter((x) => x !== s) : [...list, s]);
  };

  return (
    <CRMLayout>
      <div className="mb-6" data-testid="lead-search-page">
        <div className="flex items-center gap-2 overline text-gold">
          <Sparkles className="w-3 h-3" /> AI Lead Generation
        </div>
        <h1 className="font-display text-3xl text-navy mt-2">Lead Search <span className="text-gold text-lg ml-2">· Alberta, Canada</span></h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Our AI agents research commercial, medical, and dental lead candidates in Alberta based on your target signals — then save the best ones straight to your CRM pipeline.
        </p>
      </div>

      {/* Alberta market briefing */}
      <MarketBriefing city={mode === "property" ? prop.city : "all"} onNeighbourhoodPick={handleNeighbourhoodPick} />

      {/* Agent selector */}
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {[
          { v: "property", l: "AI Property Finder", d: "Commercial, medical, industrial, retail", Icon: Building2 },
          { v: "dental", l: "AI Dental Broker", d: "Retiring dentists, practice sales, closures", Icon: Stethoscope },
          { v: "owner", l: "AI Owner Finder", d: "Research owner/contact for a target property", Icon: Heart },
        ].map(({ v, l, d, Icon }) => (
          <button key={v} onClick={() => setMode(v)} className={`text-left p-5 border transition ${mode === v ? "border-gold bg-white shadow-luxury" : "border-slate-200 bg-white hover:border-navy"}`} data-testid={`agent-select-${v}`}>
            <Icon className="w-5 h-5 text-gold mb-3" strokeWidth={1.5} />
            <div className="font-display text-lg text-navy">{l}</div>
            <div className="text-xs text-slate-500 mt-1">{d}</div>
          </button>
        ))}
      </div>

      {/* Filters card */}
      <div className="bg-white border border-slate-200 p-6 mb-6">
        {mode === "property" && (
          <div data-testid="property-finder-form">
            <div className="mb-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold bg-gold/10 px-3 py-1.5" data-testid="pf-alberta-lock">
              <MapPin className="w-3.5 h-3.5" />
              Searching Alberta, Canada · prices in CAD
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <select required value={prop.city} onChange={(e) => { setProp({ ...prop, city: e.target.value }); setPropNbhds([]); }} className="border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="pf-city">
                {ALBERTA_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="border border-slate-200 px-3 py-2 text-sm bg-slate-50 text-slate-500 flex items-center" data-testid="pf-state-locked">Alberta (AB)</div>
              <select value={prop.property_type} onChange={(e) => setProp({ ...prop, property_type: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="pf-type">
                {PROP_TYPES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
              <input type="number" placeholder="Limit (max 12)" value={prop.limit} onChange={(e) => setProp({ ...prop, limit: Math.min(12, Number(e.target.value) || 6) })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="pf-limit" />
            </div>

            {/* Neighbourhood chips for selected city */}
            {(ALBERTA_NEIGHBOURHOODS[prop.city] || []).length > 0 && (
              <div className="mt-4" data-testid="pf-neighbourhoods">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline text-gold">{prop.city} · Neighbourhoods</div>
                  {propNbhds.length > 0 && (
                    <button onClick={() => setPropNbhds([])} className="text-xs text-slate-400 hover:text-navy" data-testid="pf-nbhd-clear">
                      Clear ({propNbhds.length})
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(ALBERTA_NEIGHBOURHOODS[prop.city] || []).map((n) => {
                    const active = propNbhds.includes(n);
                    return (
                      <button type="button" key={n}
                        onClick={() => setPropNbhds(active ? propNbhds.filter((x) => x !== n) : [...propNbhds, n])}
                        className={`text-xs px-3 py-1.5 border transition ${active ? "border-gold bg-gold text-navy font-semibold" : "border-slate-200 text-slate-600 hover:border-navy"}`}
                        data-testid={`pf-nbhd-${n.replace(/\s|\//g, "-")}`}>
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <input placeholder="Business type (optional)" value={prop.business_type} onChange={(e) => setProp({ ...prop, business_type: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="pf-business" />
              <input placeholder="Keywords — features, specialty (optional)" value={prop.keywords} onChange={(e) => setProp({ ...prop, keywords: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="pf-keywords" />
            </div>
            <div className="mt-4">
              <div className="overline text-gold mb-2">Signals</div>
              <div className="flex flex-wrap gap-2">
                {SIGNALS.map((s) => (
                  <button type="button" key={s.v} onClick={() => toggleSig(s.v, propSignals, setPropSignals)} className={`text-xs px-3 py-1.5 border ${propSignals.includes(s.v) ? "border-gold bg-slate-50 text-navy font-semibold" : "border-slate-200 text-slate-600"}`} data-testid={`pf-signal-${s.v}`}>
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {mode === "dental" && (
          <div data-testid="dental-broker-form">
            <div className="grid md:grid-cols-3 gap-3">
              <input required placeholder="City *" value={dental.city} onChange={(e) => setDental({ ...dental, city: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="db-city" />
              <input placeholder="State / Province" value={dental.state} onChange={(e) => setDental({ ...dental, state: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="db-state" />
              <input type="number" placeholder="Limit (max 12)" value={dental.limit} onChange={(e) => setDental({ ...dental, limit: Math.min(12, Number(e.target.value) || 6) })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="db-limit" />
            </div>
            <div className="mt-4">
              <div className="overline text-gold mb-2">Focus</div>
              <div className="flex flex-wrap gap-2">
                {DENTAL_SIGNALS.map((s) => (
                  <button type="button" key={s.v} onClick={() => toggleSig(s.v, dentalFocus, setDentalFocus)} className={`text-xs px-3 py-1.5 border ${dentalFocus.includes(s.v) ? "border-gold bg-slate-50 text-navy font-semibold" : "border-slate-200 text-slate-600"}`} data-testid={`db-focus-${s.v}`}>
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {mode === "owner" && (
          <div data-testid="owner-finder-form">
            <div className="grid md:grid-cols-3 gap-3">
              <input required placeholder="Business or property name *" value={owner.business_or_property_name} onChange={(e) => setOwner({ ...owner, business_or_property_name: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm md:col-span-1" data-testid="of-name" />
              <input placeholder="Address" value={owner.address} onChange={(e) => setOwner({ ...owner, address: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="of-address" />
              <input placeholder="Industry" value={owner.industry} onChange={(e) => setOwner({ ...owner, industry: e.target.value })} className="border border-slate-200 px-3 py-2 text-sm" data-testid="of-industry" />
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <div className="text-xs text-slate-400">AI-generated candidates are research starting points — always verify contact info before outreach.</div>
          <button onClick={run} disabled={busy} className="btn-navy text-sm py-2.5 px-5 disabled:opacity-50" data-testid="ai-run-btn">
            <Search className="w-4 h-4" /> {busy ? "Researching…" : "Run AI Agent"}
          </button>
        </div>
        {err && <div className="mt-3 text-sm text-rose-600" data-testid="ai-error">{err}</div>}
      </div>

      {/* Results */}
      {candidates.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-500">
              {candidates.length} candidates · {selected.size} selected
            </div>
            <button onClick={saveToLeads} disabled={selected.size === 0 || busy} className="btn-gold text-sm py-2 px-4 disabled:opacity-50" data-testid="ai-save-btn">
              <Plus className="w-4 h-4" /> Save {selected.size > 0 ? selected.size : ""} to CRM
            </button>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ai-results">
            {candidates.map((c, i) => (
              <CandidateCard key={i} c={c} kind={mode} selected={selected.has(i)} onToggle={() => toggle(i)} />
            ))}
          </div>
        </>
      )}

      {saved !== null && (
        <div className="mt-6 p-4 bg-emerald-50 border border-emerald-300 text-emerald-900 text-sm" data-testid="ai-save-success">
          ✓ {saved} lead{saved !== 1 ? "s" : ""} added to your CRM. Head to <a href="/crm/leads" className="underline font-semibold">Leads</a> to assign and action them.
        </div>
      )}

      {ownerResult && (
        <div className="bg-white border border-slate-200 p-6 space-y-5" data-testid="owner-result">
          <div>
            <div className="overline text-gold">Ownership</div>
            <div className="font-display text-xl text-navy mt-1">{ownerResult.likely_ownership_structure}</div>
          </div>
          {ownerResult.suggested_contact_channels && (
            <div>
              <div className="overline text-gold">Suggested channels</div>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {ownerResult.suggested_contact_channels.map((c, i) => <li key={i}>· {c}</li>)}
              </ul>
            </div>
          )}
          {ownerResult.candidate_contact_patterns && (
            <div>
              <div className="overline text-gold">Contact patterns to try</div>
              <div className="mt-2 grid md:grid-cols-2 gap-2">
                {ownerResult.candidate_contact_patterns.map((p, i) => (
                  <div key={i} className="bg-slate-50 p-3 text-xs">
                    <div className="font-semibold text-navy">{p.role}</div>
                    <div className="text-slate-600 mt-1">{p.email_pattern}</div>
                    <div className="text-slate-600">{p.phone_pattern}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ownerResult.research_steps && (
            <div>
              <div className="overline text-gold">Research playbook</div>
              <ol className="mt-2 space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
                {ownerResult.research_steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
          <div className="p-3 bg-amber-50 border-l-4 border-amber-400 text-xs text-amber-900">
            {ownerResult.disclaimer}
          </div>
        </div>
      )}
    </CRMLayout>
  );
};

export default LeadSearch;
